import React from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { CONTACT } from "@/lib/translations";

// Inline Pinterest icon (lucide doesn't ship one in this version)
const PinterestIcon = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="8" y1="20" x2="12" y2="11" />
    <path d="M9.5 13.5C9 11.5 9.5 8.5 12 8.5c2 0 3.5 1.5 3.5 3.5s-1.5 3.5-3 3.5c-1 0-1.5-0.7-1.5-1.5" />
  </svg>
);

const Footer = () => {
  const { t } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer
      className="bg-black text-white"
      data-testid="main-footer"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          {/* Brand */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white flex items-center justify-center rounded-sm">
                <span className="font-display text-base font-semibold tracking-tight text-black">
                  F<span className="text-[#E11D2E]">D</span>
                </span>
              </div>
              <div>
                <p className="font-display text-lg font-medium">
                  Feenstra<span className="text-[#E11D2E]"> Designs</span>
                </p>
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 mt-1">
                  {t.footer.tagline}
                </p>
              </div>
            </div>
            <div className="mt-8 inline-block w-12 h-[2px] bg-[#E11D2E]" />
          </div>

          {/* Links */}
          <div className="md:col-span-3">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-5">
              Navigatie
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <Link to="/" className="hover:text-[#E11D2E] transition-colors" data-testid="footer-home-link">
                  {t.footer.links.home}
                </Link>
              </li>
              <li>
                <Link to="/about" className="hover:text-[#E11D2E] transition-colors" data-testid="footer-about-link">
                  {t.footer.links.about}
                </Link>
              </li>
              <li>
                <Link to="/portfolio" className="hover:text-[#E11D2E] transition-colors" data-testid="footer-portfolio-link">
                  {t.footer.links.portfolio}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="hover:text-[#E11D2E] transition-colors" data-testid="footer-contact-link">
                  {t.footer.links.contact}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-4">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mb-5">
              Contact
            </p>
            <ul className="space-y-3 text-sm">
              <li>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="hover:text-[#E11D2E] transition-colors"
                  data-testid="footer-email-link"
                >
                  {CONTACT.email}
                </a>
              </li>
              <li>
                <a
                  href={`tel:${CONTACT.phoneRaw}`}
                  className="hover:text-[#E11D2E] transition-colors"
                  data-testid="footer-phone-link"
                >
                  {CONTACT.phone}
                </a>
              </li>
            </ul>
            <div className="flex items-center gap-4 mt-6">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                data-testid="footer-instagram-link"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#E11D2E] hover:text-[#E11D2E] transition-colors"
              >
                <Instagram size={16} />
              </a>
              <a
                href={CONTACT.pinterest}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Pinterest"
                data-testid="footer-pinterest-link"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#E11D2E] hover:text-[#E11D2E] transition-colors"
              >
                <PinterestIcon size={16} />
              </a>
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {year} Feenstra Designs. {t.footer.rights}
          </p>
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/30">
            Architectural Visualisation Studio · NL
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
